/**
 * Fulcrum Data Source Adapter
 *
 * This adapter contains all the Fulcrum-specific logic extracted from the original
 * dataSourceAdapters.ts file. It preserves ALL the complex functionality including:
 * - Field mapping and transformation
 * - Photo URL construction
 * - Relationship resolution
 * - Form schema processing
 * - Region detection
 * - And much more sophisticated logic
 */

import { IDataSourceAdapter, SearchOptions, SearchResult } from "./IDataSourceAdapter";
import { ApiEndpoint, StandardizedDataItem, FieldMapping, MediaItemDetailed, RelatedEntityItem } from "../../types";

// Import Fulcrum utilities
import {
  fetchFulcrumRecordsAsGeoJSON,
  fetchFulcrumRecordById,
  fetchFulcrumForm,
  fetchReverseRelationships,
  isFulcrumPhotoUrl,
  fetchFulcrumPaginatedRecords
} from "../fulcrumLibService";

// Import shared utilities
import {
  createFormToRelationshipMapping,
  resolveLinkedRecords,
  resolveLinkedRecordsWithCache,
  fetchFormSchemaWithCache,
  withPerformanceMonitoring,
  FULCRUM_FIELD_CONFIG,
  mapFormFields,
  detectFulcrumRegion,
  constructFulcrumPhotoUrl,
  geoJsonToCultureMapAdapter,
  extractUnifiedDisplayName,
  extractUnifiedDisplayDescription,
  getFirstMatchingValueCI,
  extractStringValue
} from "./SharedAdapterUtils";
import { useAuthStore } from "../../store/auth";
import { queryClient } from "../../queryClient";
import { extractFormSchema, getRefinedData } from "./fulcrumAdapterUtils/recordDataExtraction";

/**
 * When false (default): Edit opens the Fulcrum web app for that record.
 * When true: returns false from openItemForEditing so the in-app CultureMap editor is used instead.
 * Reserved for a future milestone when direct Fulcrum editing is implemented.
 */
const FULCRUM_USE_CULTUREMAP_ENTITY_EDITOR = false;

/**
 * Fulcrum web-app base URLs keyed by region.
 * Mirrors FULCRUM_BASE_URLS in fulcrumLibService.ts (API hosts) but for the
 * browser-facing web interface used when opening records for editing.
 */
const FULCRUM_WEB_BASE_URLS: Record<string, string> = {
  AU: "https://web.fulcrumapp-au.com",
  US: "https://web.fulcrumapp.com",
  EU: "https://web.fulcrumapp-eu.com"
};

/**
 * Construct Fulcrum web interface URL with proper region detection.
 *
 * Region resolution order:
 *   1. Explicit `region` argument (from user profile / auth store) — most reliable
 *   2. Form-ID length heuristic via detectFulcrumRegion — AU / US only
 */
export const constructFulcrumWebUrl = (recordId: string, formId?: string, region?: string | null): string => {
  if (!recordId) {
    throw new Error(`Invalid recordId: ${recordId}`);
  }

  const resolvedRegion = region?.toUpperCase() || detectFulcrumRegion(formId).toUpperCase();
  const baseUrl = FULCRUM_WEB_BASE_URLS[resolvedRegion] || FULCRUM_WEB_BASE_URLS.AU;
  return `${baseUrl}/records/${recordId}`;
};

export class FulcrumAdapter implements IDataSourceAdapter {
  constructor() {
    console.log("[FulcrumAdapter] NEW VERSION - Initialized without circular dependency");
  }

  // ============================================================================
  // Core Data Operations
  // ============================================================================

  /**
   * Fetches data from Fulcrum API as GeoJSON and transforms it for CultureMap.
   *
   * Fulcrum-specific behavior:
   * - Uses Fulcrum API v2 GeoJSON endpoint for spatial data with form structure
   * - Requires form_id mapping since Fulcrum organizes data by forms, not tables
   * - Handles Fulcrum's nested form_values structure with field IDs
   * - Processes embedded photos and attachments within each record
   * - No native pagination - fetches all records and handles client-side
   * - Requires region-specific API endpoints (AU vs US)
   *
   * Frontend UI Usage:
   * - DataGrid component: Loads Fulcrum form data as table rows with field mapping
   * - MapComponent: Displays Fulcrum records as GeoJSON features on map layers
   * - SearchBar: Provides search across Fulcrum form records (client-side filtering)
   * - Used when displaying field survey data, cultural sites, or any Fulcrum-collected data
   *
   * @param endpoint - Fulcrum endpoint configuration with formId and region info
   * @param userProfile - User profile with Fulcrum API tokens and organization access
   * @param options - Search options (limited compared to Django due to Fulcrum API constraints)
   * @returns Promise resolving to GeoJSON features transformed to CultureMap format
   */
  async fetchEndpointData(
    endpoint: ApiEndpoint,
    userProfile: any,
    options: SearchOptions = {},
    isPaginated: boolean = true
  ): Promise<SearchResult> {
    try {
      // Early return if no user profile (e.g., during logout)
      if (!userProfile) {
        console.log(`[FulcrumAdapter] No user profile available, returning empty data for endpoint: ${endpoint.tableName}`);
        return {
          data: [],
          pagination: null
        };
      }

      // Build Fulcrum API parameters
      const params: Record<string, string> = {};

      // Add pagination
      if (options.paginationUrl) {
        // Extract page info from pagination URL
        const url = new URL(options.paginationUrl);
        const page = url.searchParams.get("page");
        if (page) params.page = page;
      }

      // Add filters
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          params[key] = value;
        });
      }

      // commenting out cause we are using the geojson endpoint
      if (isPaginated) {
        const newParams = { page: params?.page, per_page: params?.page_size };

        const [response, schema] = await Promise.all([
          fetchFulcrumPaginatedRecords(endpoint.formId || endpoint.tableName, userProfile, newParams),
          queryClient.fetchQuery({
            queryKey: ["fulcrum-form-schema", endpoint.formId],
            queryFn: async () => {
              const data = await fetchFulcrumForm(endpoint?.formId || "", userProfile);
              return extractFormSchema(data.form);
            }
          })
        ]);

        const finalData = getRefinedData(response?.[0]?.results, schema, endpoint?.dataSourceConfig || {});

        return {
          data: finalData || [],
          pagination: {
            count: response?.[0]?.count || 0,
            next: response?.[0]?.next || null,
            previous: response?.[0]?.previous || null
          }
        };
      } else {
        // Fetch GeoJSON records and the form schema concurrently.
        //
        // The schema is fetched for every endpoint that has a formId so that
        // geoJsonToCultureMapAdapter can normalise raw Fulcrum field IDs /
        // data_names to the human-readable labels defined in the form schema.
        // This is the same translation that processFullRecord performs for
        // individually-fetched records (popup, related items), making
        // name/description resolution consistent across all UI surfaces.
        //
        // React Query caches the schema result (staleTime: 10 min) so repeated
        // map navigations and sidebar loads pay no additional network cost.
        const formId = endpoint.formId || endpoint.tableName;
        const [response, formSchema] = await Promise.all([
          fetchFulcrumRecordsAsGeoJSON(formId, userProfile, params),
          endpoint.formId
            ? queryClient.fetchQuery({
                queryKey: ["fulcrum-form-schema", endpoint.formId],
                queryFn: async () => {
                  const data = await fetchFulcrumForm(endpoint.formId!, userProfile);
                  // Support both { form: {...} } and bare schema response shapes
                  return data?.form ?? data ?? null;
                },
                staleTime: 1000 * 60 * 10 // 10 minutes
              })
            : Promise.resolve(null)
        ]);
        // Use the proven geoJsonToCultureMapAdapter from SharedAdapterUtils.
        // Passing `formSchema` enables per-feature property normalisation so that
        // field IDs/data_names are translated to schema labels before extraction.
        if (response.features && Array.isArray(response.features)) {
          const processedData = geoJsonToCultureMapAdapter(response, endpoint, formSchema ?? undefined);
          console.log(
            `[FulcrumAdapter] Processed ${processedData.length} GeoJSON features` + ` (schema ${formSchema ? "applied" : "unavailable"})`
          );
          return {
            data: processedData,
            pagination: null // Fulcrum returns all records in one GeoJSON response
          };
        }
        return {
          data: [],
          pagination: null
        };
      }
    } catch (error) {
      console.error("[FulcrumAdapter] Error fetching endpoint data:", error);
      throw error;
    }
  }

  /**
   * Searches Fulcrum data using client-side filtering (Fulcrum API limitation).
   *
   * Fulcrum-specific constraints:
   * - Fulcrum API v2 has no built-in search functionality
   * - Must fetch all records first, then filter client-side by name/description
   * - Less efficient than Django's database-level search but necessary for Fulcrum
   * - Searches through processed field names (not raw Fulcrum field IDs)
   * - Performance depends on form size - larger forms take longer to search
   *
   * Frontend UI Usage:
   * - SearchBar component: Enables search within Fulcrum forms (though slower than Django)
   * - DataGrid search: Filters Fulcrum table data by search terms
   * - Quick find functionality: Locates specific survey records or cultural sites
   * - Used when user needs to find specific Fulcrum records by content
   *
   * @param endpoint - Fulcrum endpoint with formId
   * @param searchTerm - Text to search for in record names and descriptions
   * @param userProfile - User profile with Fulcrum API access
   * @param options - Additional options (limited effectiveness with client-side search)
   * @returns Promise resolving to client-side filtered results
   */
  async searchEndpoint(endpoint: ApiEndpoint, searchTerm: string, userProfile: any, options: SearchOptions = {}): Promise<SearchResult> {
    // Fulcrum doesn't have built-in search, so we fetch all and filter client-side
    // This matches the original implementation behavior
    const allData = await this.fetchEndpointData(endpoint, userProfile, options);

    if (!searchTerm.trim()) {
      return allData;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filteredData = allData.data.filter((item: any) => {
      const name = this.getDataItemName(item, endpoint).toLowerCase();
      const description = this.getDataItemDescription(item, endpoint).toLowerCase();

      return name.includes(searchTermLower) || description.includes(searchTermLower);
    });

    return {
      data: filteredData,
      pagination: allData.pagination
    };
  }

  /**
   * LEVEL 1: Fetches minimal Fulcrum record data optimized for fast loading.
   *
   * Fulcrum-specific optimizations:
   * - Fetches individual record by UUID from Fulcrum API
   * - Processes only essential fields to minimize transformation overhead
   * - Skips complex field mapping and relationship processing for speed
   * - Returns basic structure suitable for dropdowns and quick previews
   * - Still requires full API call since Fulcrum doesn't support field filtering
   *
   * Frontend UI Usage:
   * - OptionsContext: Populates dropdown lists for MultiSelectDialog with Fulcrum records
   * - SearchBar results: Quick preview cards for Fulcrum survey data
   * - DataGrid initial load: Fast rendering before full field processing
   * - Used when user needs quick access to Fulcrum record names without full details
   *
   * @param itemId - Fulcrum record UUID (always string format)
   * @param endpoint - Fulcrum endpoint configuration with formId
   * @param userProfile - User profile with Fulcrum API tokens
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to minimal Fulcrum record data
   */
  async fetchItemSummary(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<any> {
    // LEVEL 1: Fetch minimal item data (name, description, thumbnail only)
    try {
      if (onProgress) {
        onProgress("Fetching item summary", 0, 2);
      }

      const fullRecord = await fetchFulcrumRecordById(itemId.toString(), userProfile);

      if (onProgress) {
        onProgress("Processing summary", 1, 2);
      }

      if (!fullRecord) {
        throw new Error(`Fulcrum record ${itemId} not found`);
      }

      // Process minimal data only - no relationships, minimal field mapping
      const summary = await this.processFullRecord(
        fullRecord,
        undefined, // No field mapping needed for summary
        [], // No API endpoints needed
        userProfile,
        undefined, // No progress callback for internal processing
        false // No relationships
      );

      // Return only essential fields for summary
      const summaryItem = {
        id: summary.id,
        name: summary.name,
        description: summary.description,
        thumbnail: summary.thumbnail,
        primary_image: summary.primary_image,
        created_at: summary.created_at,
        last_updated_at: summary.last_updated_at,
        geometry: summary.geometry,
        coordinates: summary.coordinates,
        entityType: endpoint.tableName,
        isComplete: false, // Summary level is not complete
        _source: summary._source
      };

      if (onProgress) {
        onProgress("Summary complete", 2, 2);
      }

      console.log(`[FulcrumAdapter] ✅ Fetched summary for item ${itemId}: "${summaryItem.name}"`);
      return summaryItem;
    } catch (error) {
      console.error(`[FulcrumAdapter] Error fetching item summary ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * LEVEL 2: Fetches complete Fulcrum record with full field processing but no relationships.
   *
   * Fulcrum-specific processing:
   * - Fetches individual record by UUID and processes all form_values
   * - Performs complex field ID to readable name mapping using form schema
   * - Extracts and processes embedded photos as part of the record structure
   * - Creates comprehensive field display data for IntelligentFieldDisplay
   * - Photos are included by default since they're embedded in Fulcrum records
   * - No separate relationship processing step needed (unlike Django)
   *
   * Frontend UI Usage:
   * - DataItemDetails: Shows complete Fulcrum survey data with readable field names
   * - SelectionContext: Default fetch when user clicks Fulcrum records on map or DataGrid
   * - IntelligentFieldDisplay: Renders all form fields with proper labels and formatting
   * - MediaDrawer: Displays embedded photos from Fulcrum photo fields
   * - Used when user needs to see complete survey response with all collected data
   *
   * @param itemId - Fulcrum record UUID
   * @param endpoint - Fulcrum endpoint configuration with formId
   * @param userProfile - User profile with Fulcrum API access
   * @param allApiEndpoints - All endpoints for relationship context (unused in Level 2)
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to complete Fulcrum record with processed fields
   */
  async fetchItem(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    allApiEndpoints: ApiEndpoint[] = [],
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<any> {
    // LEVEL 2: Fetch complete item data (all properties but no relationships)
    try {
      if (onProgress) {
        onProgress("Fetching Fulcrum record", 0, 3);
      }

      const fullRecord = await fetchFulcrumRecordById(itemId.toString(), userProfile);

      if (onProgress) {
        onProgress("Processing Fulcrum record", 1, 3);
      }

      if (fullRecord) {
        // Process the record without relationships for Level 2
        const processedRecord = await this.processFullRecord(
          fullRecord,
          endpoint.dataSourceConfig?.fieldMapping,
          allApiEndpoints,
          userProfile,
          onProgress,
          false // No relationships for level 2
        );

        if (onProgress) {
          onProgress("Completed Fulcrum processing", 3, 3);
        }

        return processedRecord;
      }

      // Enhanced error with more context
      const errorMessage = `Fulcrum record ${itemId} not found`;
      console.error(`[FulcrumAdapter] ${errorMessage}`);

      throw new Error(errorMessage);
    } catch (error: any) {
      console.error(`[FulcrumAdapter] error fetching item ${itemId}:`, error);

      // Check if this is a "not found" error and try fallback
      if (error.message?.includes("not found") || error.message?.includes("404") || error.message?.includes("Fulcrum API error (404)")) {
        try {
          if (onProgress) {
            onProgress("Searching in bulk data", 2, 3);
          }

          const fallbackRecord = await this.fetchFromBulkDataFallback(itemId, endpoint, userProfile);

          if (fallbackRecord) {
            if (onProgress) {
              onProgress("Completed fallback processing", 3, 3);
            }

            return fallbackRecord;
          }
        } catch (fallbackError) {
          console.warn(`[FulcrumAdapter] fallback also failed for record ${itemId}:`, fallbackError);
        }
      }

      throw error;
    }
  }

  /**
   * LEVEL 3: Fetches complete Fulcrum record WITH cross-form relationships.
   *
   * Fulcrum-specific relationship processing:
   * - Fetches individual record with full field processing (same as Level 2)
   * - Additionally processes record link fields that reference other Fulcrum forms
   * - Resolves linked records across different forms using record_id references
   * - Handles reverse relationships where other records link to this one
   * - Most complex operation requiring multiple API calls for relationship resolution
   * - Includes comprehensive error handling with fallback mechanisms
   *
   * Frontend UI Usage:
   * - RelatedItemsRing: Shows connections between Fulcrum forms (e.g., sites linked to surveys)
   * - DataItemDetails: Displays full context when user clicks "Find Related"
   * - SelectionContext: Used when alwaysRequestRelatedItemsAndMedia config is enabled
   * - Cross-form navigation: Enables jumping between related survey records
   * - Used when user needs to see complete survey relationships and connected data
   *
   * @param itemId - Fulcrum record UUID
   * @param endpoint - Fulcrum endpoint configuration with formId
   * @param userProfile - User profile with Fulcrum API access
   * @param allApiEndpoints - All endpoints needed for cross-form relationship resolution
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to complete Fulcrum record with resolved relationships
   */
  async fetchItemWithRelationships(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    allApiEndpoints: ApiEndpoint[] = [],
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<any> {
    try {
      // Early return if no user profile (e.g., during logout)
      if (!userProfile) {
        console.log(`[FulcrumAdapter] No user profile available, cannot fetch item ${itemId}`);
        throw new Error(`No user profile available for Fulcrum record ${itemId}`);
      }

      if (onProgress) {
        onProgress("Fetching Fulcrum record", 0, 3);
      }

      const fullRecord = await fetchFulcrumRecordById(itemId.toString(), userProfile);

      if (onProgress) {
        onProgress("Processing Fulcrum record", 1, 3);
      }

      if (fullRecord) {
        // Process the record directly without circular dependency
        // LEVEL 3: Always include relationships for this method
        const processedRecord = await this.processFullRecord(
          fullRecord,
          endpoint.dataSourceConfig?.fieldMapping,
          allApiEndpoints,
          userProfile,
          onProgress,
          true // Always include relationships for level 3
        );

        if (onProgress) {
          onProgress("Completed Fulcrum processing", 3, 3);
        }

        return processedRecord;
      }

      throw new Error(`Fulcrum record ${itemId} not found`);
    } catch (error: any) {
      console.error("[FulcrumAdapter] Error fetching item with relationships:", error);

      // Check if this is a record not found error (404 or similar)
      if (error.message?.includes("not found") || error.message?.includes("404") || error.message?.includes("Fulcrum API error (404)")) {
        // Try fallback to bulk GeoJSON data for missing records
        console.warn(`[FulcrumAdapter] Record ${itemId} not found, trying fallback to bulk data...`);

        try {
          if (onProgress) {
            onProgress("Searching in bulk data", 2, 3);
          }

          const fallbackRecord = await this.fetchFromBulkDataFallback(itemId, endpoint, userProfile);

          if (fallbackRecord) {
            console.log(`[FulcrumAdapter] Successfully found record ${itemId} in bulk data`);

            if (onProgress) {
              onProgress("Completed fallback processing", 3, 3);
            }

            return fallbackRecord;
          }
        } catch (fallbackError) {
          console.warn(`[FulcrumAdapter] Fallback also failed for record ${itemId}:`, fallbackError);
        }

        // If all fallbacks fail, return a basic placeholder entity to prevent crashes
        console.warn(`[FulcrumAdapter] Creating placeholder entity for missing record ${itemId}`);
        return this.createPlaceholderEntity(itemId, endpoint, error.message);
      }

      // For network errors, also try fallback
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("ERR_INTERNET_DISCONNECTED") ||
        error.message?.includes("Network Error") ||
        error.message?.includes("503") ||
        error.message?.includes("CORS")
      ) {
        console.warn(`[FulcrumAdapter] Network error for record ${itemId}, trying fallback...`);

        try {
          if (onProgress) {
            onProgress("Retrying with bulk data", 2, 3);
          }

          const fallbackRecord = await this.fetchFromBulkDataFallback(itemId, endpoint, userProfile);

          if (fallbackRecord) {
            console.log(`[FulcrumAdapter] Successfully recovered record ${itemId} from bulk data after network error`);

            if (onProgress) {
              onProgress("Completed network fallback", 3, 3);
            }

            return fallbackRecord;
          }
        } catch (fallbackError) {
          console.warn(`[FulcrumAdapter] Network fallback failed for record ${itemId}:`, fallbackError);
        }

        // For network errors, return placeholder with network-specific message
        console.warn(`[FulcrumAdapter] Creating placeholder entity for network error on record ${itemId}`);
        return this.createPlaceholderEntity(itemId, endpoint, "Network connection issue - showing basic information");
      }

      // For all other errors, rethrow
      throw error;
    }
  }

  /**
   * Fallback method to fetch record from bulk GeoJSON data when individual fetch fails
   */
  private async fetchFromBulkDataFallback(itemId: string | number, endpoint: ApiEndpoint, userProfile: any): Promise<any> {
    if (!endpoint.formId) {
      throw new Error(`No formId available for fallback fetch of record ${itemId}`);
    }

    try {
      // Import dynamically to avoid circular dependencies
      const { fetchFulcrumRecordsAsGeoJSON } = await import("../fulcrumLibService");

      console.log(`[FulcrumAdapter] Attempting bulk data fallback for record ${itemId} from form ${endpoint.formId}`);

      // Fetch bulk GeoJSON data
      const geoJsonData = await fetchFulcrumRecordsAsGeoJSON(endpoint.formId, userProfile);

      if (!geoJsonData?.features || geoJsonData.features.length === 0) {
        throw new Error(`No bulk data available for Fulcrum form ${endpoint.formId}`);
      }

      // Find the specific feature by ID (try multiple ID field patterns)
      const targetFeature = geoJsonData.features.find((feature: any) => {
        const props = feature.properties || {};
        return (
          props.id === itemId ||
          props._record_id === itemId ||
          props.record_id === itemId ||
          props.fulcrum_id === itemId ||
          props.id === itemId.toString() ||
          props._record_id === itemId.toString() ||
          props.record_id === itemId.toString() ||
          props.fulcrum_id === itemId.toString()
        );
      });

      if (!targetFeature) {
        throw new Error(`Record ${itemId} not found in bulk GeoJSON data (searched ${geoJsonData.features.length} features)`);
      }

      console.log(`[FulcrumAdapter] Found record ${itemId} in bulk data, processing...`);

      // Process this single feature using the bulk adapter
      const singleFeatureGeoJson = {
        type: "FeatureCollection",
        features: [targetFeature]
      };

      const items = geoJsonToCultureMapAdapter(singleFeatureGeoJson, endpoint);
      if (items.length === 0) {
        throw new Error(`Failed to process record ${itemId} from bulk data`);
      }

      const item = items[0];

      // Mark as incomplete since bulk data doesn't have full relationship info
      // Ensure _source follows StandardizedDataItem structure
      const fallbackItem = {
        ...item,
        isComplete: false,
        _source: {
          type: "fulcrum" as const,
          originalId: itemId,
          rawData: {
            ...((item as any)._source?.rawData || {}),
            fromBulkFallback: true,
            fallbackReason: "Individual record fetch failed, using bulk data"
          }
        }
      };

      return fallbackItem;
    } catch (error) {
      console.error(`[FulcrumAdapter] Bulk data fallback failed for record ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Create a placeholder entity when all fetch attempts fail
   */
  private createPlaceholderEntity(itemId: string | number, endpoint: ApiEndpoint, errorMessage: string): any {
    console.log(`[FulcrumAdapter] Creating placeholder entity for ${itemId} due to: ${errorMessage}`);

    return {
      id: itemId,
      name: `Fulcrum Record ${itemId}`,
      description: "This record could not be loaded. It may have been deleted or moved.",
      isComplete: false,
      relationships: {
        related_entities: {},
        geo_entities: {},
        media: []
      },
      _source: {
        type: "fulcrum" as const,
        originalId: itemId,
        rawData: {
          error: errorMessage,
          placeholder: true
        },
        isPlaceholder: true
      },
      // Add basic properties that components might expect
      entityType: endpoint.tableName || "fulcrum-record",
      layerId: endpoint.tableName,
      coordinates: null,
      geometry: null,
      properties: {
        id: itemId,
        name: `Fulcrum Record ${itemId}`,
        description: "Record unavailable",
        error: errorMessage
      }
    };
  }

  /**
   * Process a full Fulcrum record into StandardizedDataItem format
   * This method contains the core processing logic without circular dependencies
   */
  private async processFullRecord(
    rawItem: any,
    fieldMapping?: FieldMapping,
    allApiEndpoints: ApiEndpoint[] = [],
    userProfile: any = null,
    onProgress?: (step: string, current: number, total: number) => void,
    includeRelationships: boolean = true
  ): Promise<StandardizedDataItem> {
    // ========================================================================
    // ENHANCED FIELD MAPPING - Transform Fulcrum field IDs to readable names
    // ========================================================================

    const rawFormValues = rawItem.form_values || {};
    const formValues: Record<string, any> = {};
    const formValuesPriority: Record<string, "high" | "low" | "hidden"> = {};

    // Get form schema for proper field mapping
    let formSchema: any = null;

    try {
      if (userProfile && rawItem.form_id) {
        // Use cache-aware form schema fetching
        const formResponse = await fetchFormSchemaWithCache(rawItem.form_id, userProfile);
        // Handle both response formats: direct schema or wrapped in 'form' property
        formSchema = formResponse?.form || formResponse;
      }
    } catch (error) {
      console.warn("🔄 [SCHEMA-FETCH] Could not fetch form schema for field mapping:", error);
    }

    // Use form schema mapping if available, otherwise fall back to hardcoded config
    if (formSchema && formSchema.elements?.length > 0) {
      const mappedFields = mapFormFields(rawFormValues, formSchema);

      Object.entries(mappedFields).forEach(([readableKey, value]) => {
        formValues[readableKey] = value;
        const fieldConfig = Object.values(FULCRUM_FIELD_CONFIG).find((config) => config.name === readableKey.toLowerCase());
        formValuesPriority[readableKey] = fieldConfig?.priority || "low";
      });
    } else {
      let mappedCount = 0;
      Object.entries(rawFormValues).forEach(([fieldId, value]) => {
        const fieldConfig = FULCRUM_FIELD_CONFIG[fieldId];
        if (fieldConfig) {
          const readableKey = fieldConfig.name;
          formValues[readableKey] = value;
          formValuesPriority[readableKey] = fieldConfig.priority;
          mappedCount++;
        } else {
          // Keep unmapped fields with their original IDs
          formValues[fieldId] = value;
          formValuesPriority[fieldId] = "low";
        }
      });
    }

    // ========================================================================
    // ENHANCED PHOTO PROCESSING
    // ========================================================================

    // Extract photo fields from form values
    const photoFields = Object.entries(rawFormValues).filter(
      ([key, value]) =>
        Array.isArray(value) && value.length > 0 && value.some((item: any) => item && typeof item === "object" && item.photo_id)
    );

    // Combine form values with top-level record properties for field extraction
    const allProperties = {
      ...rawItem,
      ...formValues,
      // Dynamic photo extraction - get all photo fields
      allPhotos: photoFields.length > 0 ? photoFields.flatMap(([_, photos]) => photos) : []
    };

    // Extract and transform photos into proper media objects
    const photos = allProperties.allPhotos && allProperties.allPhotos.length > 0 ? allProperties.allPhotos : allProperties.photos || [];
    let primaryImage = undefined;
    let mediaItems: any[] = [];

    if (Array.isArray(photos) && photos.length > 0) {
      // Transform each photo into a media object
      const transformedPhotos = photos.map((photo: any, index: number) => {
        const photoId = photo.photo_id;
        const caption = photo.caption || "";

        // Generate Fulcrum photo URLs using centralized URL construction
        const photoUrl = this.constructFulcrumPhotoUrl(photoId, rawItem.form_id, false);
        const thumbnailUrl = this.constructFulcrumPhotoUrl(photoId, rawItem.form_id, true);

        return {
          id: photoId,
          name: caption || `Photo ${index + 1}`,
          description: caption || `Photo from ${this.getDataItemName(rawItem)}`,
          url: photoUrl,
          thumbnail: thumbnailUrl,
          media_type: "photo",
          type: "photo",
          // Add Fulcrum-specific identifiers so getDataSourceType recognizes this as Fulcrum
          dataSourceType: "fulcrum",
          fulcrum_id: photoId,
          form_id: rawItem.form_id,
          // CRITICAL: Set entityType so these are recognized as media items
          entityType: "media",
          original_file: {
            filename: caption || `photo_${photoId}.jpg`,
            type: "image/jpeg",
            size: "unknown"
          },
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          // Include Fulcrum-specific metadata
          fulcrum_photo: {
            photo_id: photoId,
            caption: caption,
            record_id: rawItem.id,
            form_id: rawItem.form_id
          },
          metadata: {
            photo_index: index,
            form_id: rawItem.form_id
          }
        };
      });

      // Set primary image from first photo
      if (transformedPhotos.length > 0) {
        const firstPhoto = transformedPhotos[0];
        primaryImage = {
          id: firstPhoto.id,
          url: firstPhoto.url,
          thumbnail: firstPhoto.thumbnail,
          name: firstPhoto.name,
          description: firstPhoto.description
        };
      }

      // Put all photos in media array
      mediaItems = transformedPhotos;
    }

    // ========================================================================
    // DISPLAY NAME AND DESCRIPTION EXTRACTION
    // ========================================================================

    // Extract display name and description using unified helpers
    // CRITICAL: This must happen AFTER field mapping so formValues contains readable field names
    const currentEp = allApiEndpoints.find((ep) => ep.formId === rawItem?.form_id);

    const displayName = extractUnifiedDisplayName({
      endpoint: currentEp,
      formValues, // This now contains the processed readable field names
      rawItem,
      fieldMapping: fieldMapping || this.getDefaultFulcrumMapping(),
      userProfile,
      formSchema
    });

    const displayDescription = extractUnifiedDisplayDescription({
      endpoint: currentEp,
      formValues, // This now contains the processed readable field names
      rawItem,
      fieldMapping: fieldMapping || this.getDefaultFulcrumMapping(),
      userProfile,
      formSchema
    });

    // ========================================================================
    // CREATE BASE ITEM WITH COMPREHENSIVE DATA
    // ========================================================================

    const geometry = rawItem.geometry;
    const coordinates = geometry?.type === "Point" ? geometry.coordinates : null;

    // Creating final standardized item with all preserved data
    // IMPORTANT: ...formValues is spread first so that field labels like "Name" or "Description"
    // (which may hold nested Fulcrum objects) do NOT overwrite the computed display strings.
    const baseItem: StandardizedDataItem = {
      // ✅ SPREAD MAPPED FIELDS TO TOP LEVEL FOR IntelligentFieldDisplay (must come before name/description)
      ...formValues,

      id: rawItem.id, // Preserve original Fulcrum UUID string
      name: displayName,
      description: displayDescription,
      thumbnail: primaryImage?.thumbnail, // Set thumbnail from primary image
      created_at: rawItem.created_at || null,
      last_updated_at: rawItem.updated_at || null,
      geometry: geometry,
      coordinates: coordinates,

      // Add primary image
      primary_image: primaryImage,

      // ✅ FULCRUM-SPECIFIC DATA PRESERVATION
      fulcrum_data: {
        form_id: rawItem.form_id,
        created_by: rawItem.created_by,
        updated_by: rawItem.updated_by,
        form_values: formValues, // ✅ TRANSFORMED FORM VALUES WITH READABLE FIELD NAMES
        raw_form_values: rawFormValues, // ✅ ORIGINAL FORM VALUES WITH FIELD IDs (for debugging)
        photos: allProperties.photos, // Keep raw photo data
        mapped_fields: allProperties, // ✅ ALL MAPPED FIELDS PRESERVED HERE
        // Store full media objects here for MediaDrawer to access
        fullMediaObjects: mediaItems,
        // ADD: Raw item for complete debugging
        raw_item: rawItem,
        // ADD: Processing metadata
        processing_metadata: {
          adapter_version: "3.0",
          processed_at: new Date().toISOString(),
          total_form_fields: Object.keys(formValues).length,
          photo_fields_found: photoFields.length,
          high_priority_fields: Object.values(formValuesPriority).filter((p) => p === "high").length
        },
        // ✅ FIELD PRIORITY MAPPING FOR UI FILTERING (as separate property to avoid type issues)
        form_values_priority: formValuesPriority
      } as any,

      relationships: {
        related_entities: {},
        geo_entities: {},
        // Convert media objects to RelatedEntityItem format for the relationships structure
        media: mediaItems.map((mediaItem: any, index: number) => ({
          id: mediaItem.id, // Preserve original photo ID (UUID string)
          entity_name: mediaItem.name,
          entity_description: mediaItem.description,
          rel_description: `Photo ${index + 1}`,
          is_primary: index === 0,
          entity_thumbnail: mediaItem.thumbnail,
          // CRITICAL: Add Fulcrum identifiers so these photos are properly detected as Fulcrum items
          fulcrum_photo: mediaItem.fulcrum_photo,
          fulcrum_data: { form_id: rawItem.form_id },
          _source: { type: "fulcrum" },
          dataSourceType: "fulcrum",
          media_type: "photo",
          // Add entityType so DataGrid knows these are media items
          entityType: "media"
        }))
      },
      _source: {
        type: "fulcrum",
        originalId: rawItem.id,
        rawData: rawItem
      }
    };

    // Process relationships if requested
    if (includeRelationships) {
      if (onProgress) {
        onProgress("Processing relationships", 2, 3);
      }

      try {
        // Extract record link fields from form values
        const recordLinkFields = this.extractRecordLinkFields(rawItem);

        if (recordLinkFields.length > 0) {
          // Create form to relationship mapping
          const formToRelationshipMapping = createFormToRelationshipMapping(allApiEndpoints);

          // Get form schema for field mapping
          let formSchema: any = null;
          try {
            if (userProfile && rawItem.form_id) {
              // Use cache-aware form schema fetching for relationship processing
              formSchema = await fetchFormSchemaWithCache(rawItem.form_id, userProfile);
            }
          } catch (error) {
            console.warn("[FulcrumAdapter] Could not fetch form schema for relationship processing:", error);
          }

          // Resolve linked records to get actual entity details using cache-aware version
          const resolvedRelationships = await withPerformanceMonitoring("resolveLinkedRecordsWithCache", () =>
            resolveLinkedRecordsWithCache(recordLinkFields, allApiEndpoints, formToRelationshipMapping, userProfile, formSchema)
          );

          // Add resolved relationships to the base item
          baseItem.relationships!.related_entities = resolvedRelationships;

          console.log(`[FulcrumAdapter] Successfully processed relationships:`, Object.keys(resolvedRelationships));
        } else {
          console.log("[FulcrumAdapter] No record link fields found in form values");
        }

        // Process reverse relationships if supported
        if (rawItem.form_id && userProfile) {
          const currentEndpoint = allApiEndpoints.find((ep) => ep.formId === rawItem.form_id);
          if (currentEndpoint?.reverseRelationships) {
            try {
              console.log(`[FulcrumAdapter] Fetching reverse relationships for ${currentEndpoint.name} record ${rawItem.id}`);

              const reverseRecords = await fetchReverseRelationships(
                rawItem.id,
                currentEndpoint.reverseRelationships,
                userProfile,
                onProgress
              );

              if (reverseRecords.length > 0) {
                console.log(`[FulcrumAdapter] Found ${reverseRecords.length} reverse relationship records`);

                // Group reverse records by their relationship key
                const reverseRelationships: Record<string, RelatedEntityItem[]> = {};

                for (const reverseRecord of reverseRecords) {
                  const relationshipMeta = reverseRecord._reverse_relationship;

                  if (relationshipMeta) {
                    const relationshipKey = relationshipMeta.relationshipKey;

                    // Use pre-extracted name from fetchReverseRelationships if available
                    let recordName = reverseRecord.entity_name || "Unknown Record";

                    const reverseItem: RelatedEntityItem = {
                      id: reverseRecord.id,
                      entity_name: recordName,
                      entity_description: reverseRecord.entity_description || "",
                      rel_description: relationshipMeta.description || `Reverse ${relationshipKey}`,
                      is_primary: false,
                      entity_thumbnail: reverseRecord.entity_thumbnail || null
                    };

                    if (!reverseRelationships[relationshipKey]) {
                      reverseRelationships[relationshipKey] = [];
                    }
                    reverseRelationships[relationshipKey].push(reverseItem);
                  }
                }

                // Merge reverse relationships with forward relationships
                Object.entries(reverseRelationships).forEach(([key, items]) => {
                  if (!baseItem.relationships!.related_entities![key]) {
                    baseItem.relationships!.related_entities![key] = [];
                  }
                  baseItem.relationships!.related_entities![key].push(...items);
                });

                console.log(`[FulcrumAdapter] Successfully processed reverse relationships:`, Object.keys(reverseRelationships));
              }
            } catch (error) {
              console.error("[FulcrumAdapter] Error fetching reverse relationships:", error);
            }
          }
        }
      } catch (error) {
        console.error("[FulcrumAdapter] Error processing relationships:", error);
      }
    }

    return baseItem;
  }

  // ============================================================================
  // Display Methods - Using the sophisticated extraction logic
  // ============================================================================

  getDataItemName(item: any, endpoint?: ApiEndpoint): string {
    if (!item) return "Unknown Item";

    // IMPORTANT: For processed items, the display name is already computed during processFullRecord()
    // and stored in item.name. We should use that first.
    if (item.name && typeof item.name === "string" && item.name !== "Unknown Item") {
      return item.name;
    }

    // If no processed name available, use our fallback extraction
    return this.getSimpleFulcrumName(item, endpoint);
  }

  getDataItemDescription(item: any, endpoint?: ApiEndpoint): string {
    if (!item) return "";

    // IMPORTANT: For processed items, the description is already computed during processFullRecord()
    // and stored in item.description. We should use that first.
    if (item.description && typeof item.description === "string") {
      return item.description;
    }

    // If no processed description available, use our fallback extraction
    return this.getSimpleFulcrumDescription(item, endpoint);
  }

  getDataItemPrimaryImage(item: any): string | null {
    if (!item) return null;

    // Check primary_image first (standardized format)
    if (item.primary_image?.url) {
      return item.primary_image.url;
    }

    // Check for thumbnail field
    if (item.thumbnail && typeof item.thumbnail === "string") {
      return item.thumbnail;
    }

    // IMPORTANT: For Fulcrum items, check form media objects (this was missing!)
    if (item.fulcrum_data?.fullMediaObjects?.length > 0) {
      const firstMedia = item.fulcrum_data.fullMediaObjects[0];
      return firstMedia.url || firstMedia.thumbnail || null;
    }

    // Check for Fulcrum photos using the original logic
    const photos = this.extractFulcrumPhotos(item);
    if (photos.length > 0) {
      const firstPhoto = photos[0];
      return this.constructFulcrumPhotoUrl(firstPhoto.id, this.getFormId(item), false);
    }

    // For media items, use the url
    if (item.url && this.isImageUrl(item.url)) {
      return item.url;
    }

    return null;
  }

  getItemThumbnail(item: any, endpoint?: any): string | null {
    if (!item) return null;

    // IMPORTANT: Check fulcrum_data.fullMediaObjects first (processed media)
    if (item.fulcrum_data?.fullMediaObjects && Array.isArray(item.fulcrum_data.fullMediaObjects)) {
      const firstMedia = item.fulcrum_data.fullMediaObjects[0];
      if (firstMedia) {
        const thumbnailUrl = firstMedia.thumbnail || firstMedia.url;
        return thumbnailUrl;
      }
    }

    // Check standardized thumbnail
    if (item.thumbnail) {
      return item.thumbnail;
    }

    // Check for Fulcrum photos (raw data)
    const photos = this.extractFulcrumPhotos(item);
    if (photos.length > 0) {
      const firstPhoto = photos[0];
      // Support both id and photo_id (raw Fulcrum form_values use photo_id)
      const photoId = firstPhoto.id || firstPhoto.photo_id;

      // Check if the photoId is already a URL (this would cause duplication)
      if (photoId && !photoId.startsWith("http")) {
        const constructedUrl = this.constructFulcrumPhotoUrl(photoId, this.getFormId(item), true); // true = thumbnail
        return constructedUrl; // Return raw Fulcrum URL - FulcrumImage component will handle authentication
      }
    }

    // Check primary_image
    if (item.primary_image?.thumbnail) {
      return item.primary_image.thumbnail;
    }

    return null;
  }

  // ============================================================================
  // Media & Relationships
  // ============================================================================

  /**
   * Extracts media items from a Fulcrum record using Fulcrum's embedded photo structure.
   *
   * Fulcrum-specific media handling:
   * - Photos are embedded directly in form_values as photo fields with photo_ids
   * - Prioritizes processed fullMediaObjects from form field processing
   * - Falls back to relationships.media structure for standardized access
   * - Constructs authenticated Fulcrum photo URLs using photo_id and form_id
   * - Handles multiple photo fields within a single record
   * - Returns standardized format compatible with MediaDrawer authentication system
   *
   * Frontend UI Usage:
   * - MediaDrawer: Displays embedded Fulcrum survey photos with authentication
   * - DataItemDetails: Shows primary photo from survey responses
   * - MediaViewer: Opens full-size authenticated Fulcrum photos
   * - Used when user needs to view photos collected during field surveys
   *
   * @param item - Fulcrum record that may contain embedded photos
   * @returns Array of standardized media items with Fulcrum photo URLs
   */
  getRelatedMedia(item: any): MediaItemDetailed[] {
    if (!item) return [];

    // PRIORITY 1: Fulcrum items store photos in fulcrum_data.fullMediaObjects (complete processing)
    if (item.fulcrum_data?.fullMediaObjects && Array.isArray(item.fulcrum_data.fullMediaObjects)) {
      // Ensure all media objects have Fulcrum identifiers for proper routing
      return item.fulcrum_data.fullMediaObjects.map((mediaItem: any) => ({
        ...mediaItem,
        dataSourceType: "fulcrum",
        fulcrum_id: mediaItem.fulcrum_id || mediaItem.id,
        form_id: mediaItem.form_id || item.fulcrum_data?.form_id || item.form_id,
        // CRITICAL: Ensure entityType is set for proper detection
        entityType: "media"
      }));
    }

    // PRIORITY 2: Look for photos in relationships.media (raw Fulcrum photos)
    if (item.relationships?.media && Array.isArray(item.relationships.media)) {
      // Ensure all media objects have Fulcrum identifiers for proper routing
      return item.relationships.media.map((mediaItem: any) => ({
        ...mediaItem,
        dataSourceType: "fulcrum",
        fulcrum_id: mediaItem.fulcrum_id || mediaItem.id,
        form_id: mediaItem.form_id || item.fulcrum_data?.form_id || item.form_id,
        // CRITICAL: Ensure entityType is set for proper detection
        entityType: "media"
      }));
    }

    // PRIORITY 3: Final fallback: if primary_image exists and has valid photo_id (not "thumbnail")
    if (item.primary_image && item.primary_image.fulcrum_photo?.photo_id && item.primary_image.fulcrum_photo.photo_id !== "thumbnail") {
      console.log(`[FulcrumAdapter] Using primary_image as single media item for Fulcrum item:`, {
        itemId: item.id,
        itemName: item.name,
        photoId: item.primary_image.fulcrum_photo.photo_id
      });
      // Ensure primary image has Fulcrum identifiers for proper routing
      return [
        {
          ...item.primary_image,
          dataSourceType: "fulcrum",
          fulcrum_id: item.primary_image.fulcrum_photo.photo_id,
          form_id: item.primary_image.fulcrum_photo.form_id || item.fulcrum_data?.form_id || item.form_id,
          // CRITICAL: Ensure entityType is set for proper detection
          entityType: "media"
        }
      ];
    }

    // PRIORITY 4: Legacy fallback: Extract Fulcrum photos using the original complex logic
    const media: MediaItemDetailed[] = [];
    const photos = this.extractFulcrumPhotos(item);
    const formId = this.getFormId(item);

    if (photos.length > 0) {
      console.log(`[FulcrumAdapter] Using legacy photo extraction for Fulcrum item:`, {
        itemId: item.id,
        itemName: item.name,
        photosFound: photos.length
      });

      photos.forEach((photo: any, index: number) => {
        if (photo && photo.id) {
          media.push({
            id: photo.id,
            name: photo.caption || `Photo ${index + 1}`,
            description: photo.caption || "",
            type: "image",
            media_type: "image",
            url: this.constructFulcrumPhotoUrl(photo.id, formId, false),
            thumbnail: this.constructFulcrumPhotoUrl(photo.id, formId, true),
            // Add Fulcrum-specific identifiers so getDataSourceType recognizes this as Fulcrum
            dataSourceType: "fulcrum",
            fulcrum_id: photo.id,
            form_id: formId,
            fulcrum_photo: photo,
            // CRITICAL: Ensure entityType is set for proper detection
            entityType: "media",
            original_file: {
              filename: photo.caption || `photo_${photo.id}.jpg`,
              type: "image/jpeg",
              size: "unknown"
            },
            relationships: {
              related_entities: {},
              geo_entities: {},
              media: []
            },
            metadata: {
              fulcrum_photo: photo,
              form_id: formId,
              photo_index: index
            }
          });
        }
      });
    }

    console.log(`[FulcrumAdapter] No media found for Fulcrum item:`, {
      itemId: item.id,
      itemName: item.name
    });
    return media;
  }

  // ============================================================================
  // Map Operations
  // ============================================================================

  /**
   * Enhances map click data for Fulcrum GeoJSON features to work with 3-level adapter system.
   *
   * Fulcrum-specific map handling:
   * - Extracts Fulcrum record UUID from GeoJSON feature properties
   * - Handles multiple ID field patterns (_record_id, record_id, fulcrum_id, id)
   * - Creates basic entity structure for SelectionContext to trigger Level 2 fetch
   * - Preserves spatial data (geometry, coordinates) from GeoJSON
   * - Sets hasCompleteData: false to ensure progressive loading
   * - Maps to endpoint.tableName for consistent entity typing
   *
   * Frontend UI Usage:
   * - MapComponent: Handles clicks on Fulcrum survey points/polygons
   * - SelectionContext: Triggers Level 2 fetch to show complete survey data
   * - DataItemDetails: Displays survey response when user clicks map features
   * - Used when user clicks on field survey locations, cultural sites, or monitoring points
   *
   * @param feature - GeoJSON feature from Fulcrum map layer
   * @param layerId - Map layer identifier
   * @param endpoint - Fulcrum endpoint configuration with formId
   * @param config - Optional configuration (unused for Fulcrum)
   * @returns Promise resolving to basic entity for SelectionContext processing
   */
  async enhanceMapClickData(feature: any, layerId: string, endpoint?: ApiEndpoint, config?: any): Promise<any> {
    // Extract ID from map click, return basic entity for SelectionContext to fetch using new 3-level system
    const properties = feature.properties || {};
    const formId = properties.form_id || endpoint?.formId || this.getFormId(feature);

    // Extract the Fulcrum record ID from the feature
    const fulcrumId =
      properties._record_id || // Fulcrum GeoJSON standard field
      properties.record_id || // Alternative record ID field
      properties.fulcrum_id || // Alternative Fulcrum ID field
      properties.id || // Standard ID field
      feature.id; // Feature ID fallback

    // Find first photo from feature properties so popup can show thumbnail before Level 2 fetch
    // (same sources as geoJsonToCultureMapAdapter / extractFulcrumPhotos)
    let thumbnail: string | undefined;
    let primaryImage: { url: string; thumbnail: string } | undefined;
    let normalizedPhotos: any[] | undefined;
    const photoFields = Object.entries(properties).filter(
      ([key, value]) =>
        (key.toLowerCase().includes("photo") &&
          typeof value === "string" &&
          value.length > 0 &&
          value !== "," &&
          !key.toLowerCase().includes("caption") &&
          !key.toLowerCase().includes("url")) ||
        (Array.isArray(value) && value.length > 0 && value.some((p: any) => p && typeof p === "object" && (p.photo_id || p.id)))
    );
    if (photoFields.length > 0) {
      const photoData = photoFields[0][1];
      let photoId: string | null = null;
      if (Array.isArray(photoData) && photoData.length > 0) {
        const first = photoData[0];
        photoId = first?.photo_id || first?.id || null;
        normalizedPhotos = photoData
          .map((p: any) => (p && typeof p === "object" ? { ...p, id: p.id || p.photo_id } : null))
          .filter(Boolean);
      } else if (typeof photoData === "string" && !photoData.includes("http") && !photoData.includes("/photos/")) {
        const ids = photoData
          .split(",")
          .map((s: string) => s.trim())
          .filter((s: string) => s.length >= 8 && /^[a-f0-9-]{8,}$/i.test(s));
        if (ids.length > 0) {
          photoId = ids[0];
          normalizedPhotos = ids.map((id: string) => ({ id, photo_id: id }));
        }
      }
      if (photoId && formId) {
        thumbnail = this.constructFulcrumPhotoUrl(photoId, formId, true);
        primaryImage = { url: this.constructFulcrumPhotoUrl(photoId, formId, false), thumbnail };
      }
    }

    // Respect endpoint dataSourceConfig.fieldMapping (config.json) – e.g. name: ["trip_name"], description: ["trip_summary"]
    // Use case-insensitive matching so "trip_name" / "trip_summary" match "Trip Name" etc.
    const descFields = endpoint?.dataSourceConfig?.fieldMapping?.description;
    const descMappingArray = descFields ? (Array.isArray(descFields) ? descFields : [descFields]) : [];
    const descFromConfig = descMappingArray.length ? getFirstMatchingValueCI(properties, descMappingArray) : null;
    const description =
      descFromConfig != null && extractStringValue(descFromConfig).trim()
        ? extractStringValue(descFromConfig).trim()
        : (typeof properties.comments === "string" && properties.comments) ||
          (typeof properties.description === "string" && properties.description) ||
          (typeof properties.notes === "string" && properties.notes) ||
          (typeof properties.comment === "string" && properties.comment) ||
          (typeof properties["7153"] === "string" && properties["7153"]) ||
          (typeof properties["68af"] === "string" && properties["68af"]) ||
          (typeof properties["631e"] === "string" && properties["631e"]) ||
          "";

    const nameFields = endpoint?.dataSourceConfig?.fieldMapping?.name;
    const nameMappingArray = nameFields ? (Array.isArray(nameFields) ? nameFields : [nameFields]) : [];
    const nameFromConfig = nameMappingArray.length ? getFirstMatchingValueCI(properties, nameMappingArray) : null;
    const nameFromProps =
      nameFromConfig != null && extractStringValue(nameFromConfig).trim()
        ? extractStringValue(nameFromConfig).trim()
        : (typeof properties.site_name === "string" && properties.site_name) ||
          (typeof properties.name === "string" && properties.name) ||
          (typeof properties.title === "string" && properties.title) ||
          (typeof properties.label === "string" && properties.label) ||
          (typeof properties.job_description === "string" && properties.job_description) ||
          (typeof properties.job_id === "string" && properties.job_id);

    console.log("🗺️ [FulcrumAdapter] Map click - extracting ID for 3-level fetch system:", {
      layerId,
      extractedId: fulcrumId,
      endpointName: endpoint?.name
    });

    const enhancedProperties: Record<string, any> = {
      ...properties,
      fromMapClick: true,
      hasCompleteData: false, // This will trigger SelectionContext to use fetchItem (Level 2)
      dataSourceType: "fulcrum",
      form_id: formId
    };
    if (normalizedPhotos && normalizedPhotos.length > 0) {
      enhancedProperties.photos = normalizedPhotos;
    }
    if (description) {
      enhancedProperties.description = description;
    }
    if (nameFromProps) {
      enhancedProperties.name = nameFromProps;
    }

    return {
      id: fulcrumId || "unknown",
      name: nameFromProps || (fulcrumId ? "Loading..." : this.getDataItemName(feature, endpoint)), // Show loading for valid IDs
      entityType: endpoint?.tableName || layerId,
      layerId: layerId,
      coordinates: feature.geometry?.type === "Point" ? (feature.geometry.coordinates as [number, number]) : undefined,
      geometry: feature.geometry,
      ...(description && { description }),
      ...(thumbnail && { thumbnail }),
      ...(primaryImage && { primary_image: primaryImage }),
      properties: enhancedProperties
    };
  }

  extractFeatureData(properties: any, layerId: string, endpoint?: ApiEndpoint): any {
    // Handle Fulcrum-specific field extraction using the established ID hierarchy pattern
    const fulcrumId =
      properties._record_id || // Fulcrum GeoJSON standard field
      properties.record_id || // Alternative record ID field
      properties.fulcrum_id || // Alternative Fulcrum ID field
      properties.id; // Standard ID field

    console.log("[FulcrumAdapter] Extracting feature data:", {
      hasRecordId: !!properties._record_id,
      hasId: !!properties.id,
      extractedId: fulcrumId,
      extractedName: properties.name,
      allProperties: Object.keys(properties)
    });

    if (fulcrumId) {
      // Extract name with priority for common Fulcrum display fields
      const fulcrumName = properties.name || properties.title || properties._title || properties.label || `${layerId} Record`;

      return {
        id: fulcrumId,
        name: fulcrumName,
        description: properties.description || properties.notes || properties.comment || "",
        extractionMethod: "fulcrum",
        layerId,
        dataSourceType: "fulcrum",
        tableName: endpoint?.tableName || layerId,
        form_id: properties.form_id || endpoint?.formId,
        ...properties // Include all original properties
      };
    }

    // Fallback if no proper ID found
    console.warn("[FulcrumAdapter] No valid Fulcrum ID found in properties:", Object.keys(properties));
    return {
      id: properties.name || "unknown", // Last resort fallback
      name: properties.name || `${layerId} Feature`,
      description: properties.description || "",
      extractionMethod: "fallback",
      layerId,
      dataSourceType: "fulcrum",
      tableName: endpoint?.tableName || layerId,
      form_id: properties.form_id || endpoint?.formId,
      ...properties
    };
  }

  // ============================================================================
  // Data Transformation
  // ============================================================================

  async adaptDataSource(rawData: any, fieldMapping?: FieldMapping, apiEndpoints?: ApiEndpoint[]): Promise<StandardizedDataItem[]> {
    // Handle Fulcrum GeoJSON response format
    if (rawData.features && Array.isArray(rawData.features)) {
      return Promise.all(rawData.features.map((feature: any) => this.processFullRecord(feature, fieldMapping, apiEndpoints || [], null)));
    }

    // Handle direct array of records
    if (Array.isArray(rawData)) {
      return Promise.all(rawData.map((item: any) => this.processFullRecord(item, fieldMapping, apiEndpoints || [], null)));
    }

    // Single item
    return [await this.processFullRecord(rawData, fieldMapping, apiEndpoints || [], null)];
  }

  async adaptFulcrumItem(
    rawItem: any,
    fieldMapping?: FieldMapping,
    apiEndpoints?: ApiEndpoint[],
    userProfile?: any,
    onProgress?: (step: string, current: number, total: number) => void,
    includeRelationships?: boolean
  ): Promise<StandardizedDataItem> {
    // Use our internal processing method to avoid circular dependency
    return this.processFullRecord(rawItem, fieldMapping, apiEndpoints, userProfile, onProgress, includeRelationships);
  }

  // ============================================================================
  // Media Entity Methods
  // ============================================================================

  canMediaItemBeStandaloneEntity(mediaItem: any): boolean {
    // Fulcrum media items are just photo attachments, not standalone entities
    return false;
  }

  canFetchMediaItemIndependently(mediaItem: any): boolean {
    // Fulcrum photos cannot be fetched as independent records - they're just file references
    return false;
  }

  canMediaItemHaveRelationships(mediaItem: any): boolean {
    // Fulcrum photos are just attachments and don't have their own relationships
    return false;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  getDataSourceType(item?: any, endpoint?: any): string {
    return "fulcrum";
  }

  isEndpointEditable(endpoint: { dataSourceType?: string; isEditable?: boolean }): boolean {
    // Fulcrum endpoints support editing through web interface
    return true;
  }

  /**
   * Opens Fulcrum item for editing.
   *
   * Controlled by FULCRUM_USE_CULTUREMAP_ENTITY_EDITOR:
   *   false (default) → opens the Fulcrum web interface in a new tab
   *   true            → returns false so the caller uses the in-app CultureMap editor
   *
   * Region is resolved from the auth store's fulcrum_region, falling back to
   * a form-ID length heuristic (AU vs US).
   *
   * @returns true if external editing was initiated, false to use internal dialog
   */
  openItemForEditing(item: any, endpoint?: ApiEndpoint | null): boolean {
    if (FULCRUM_USE_CULTUREMAP_ENTITY_EDITOR) {
      return false; // Delegate to in-app CultureMap editor
    }

    if (!item?.id) {
      console.error("[FulcrumAdapter] Cannot open item for editing - no ID found:", item);
      return false;
    }

    const formId = endpoint?.formId || item.form_id || item.fulcrum_data?.form_id;
    const recordId = item.id || item.fulcrum_id || item.source_id;

    const userRegion = useAuthStore.getState().user?.fulcrum_region ?? null;
    const editUrl = constructFulcrumWebUrl(String(recordId), formId, userRegion);

    console.log("[FulcrumAdapter] Opening Fulcrum item for editing:", {
      recordId,
      formId,
      editUrl,
      userRegion: userRegion ?? "(from form id)"
    });

    window.open(editUrl, "_blank", "noopener,noreferrer");
    return true;
  }

  isImageUrl(url: string | null): boolean {
    if (!url || typeof url !== "string") return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff)(\?|$)/i;
    if (imageExtensions.test(url)) return true;

    // Check for data URLs
    if (url.startsWith("data:image/")) return true;

    // Check for blob URLs
    if (url.startsWith("blob:")) return true;

    // Check for Fulcrum photo URLs
    if (url.includes("fulcrumapp") && url.includes("/photos/")) return true;

    return false;
  }

  async findCorrectEndpointForItem(
    itemId: string | number,
    itemName: string,
    apiEndpoints: ApiEndpoint[],
    currentEndpoint: ApiEndpoint
  ): Promise<ApiEndpoint> {
    // For Fulcrum, we need to find the endpoint that matches the form_id
    // This is a simplified version - the original has more complex logic
    return currentEndpoint;
  }

  detectFulcrumRegion(formId?: string): "au" | "us" {
    // Use the shared implementation
    return detectFulcrumRegion(formId);
  }

  getThumbnailHandler(dataSourceType: string): (item: any) => string | undefined {
    return (item: any) => {
      return this.getItemThumbnail(item) || undefined;
    };
  }

  // ============================================================================
  // Private Helper Methods - Extracted from original implementation
  // ============================================================================

  /**
   * Extract record link fields from Fulcrum form values
   * Returns an array of [fieldId, links] pairs for relationship processing
   */
  private extractRecordLinkFields(rawItem: any): [string, any[]][] {
    const recordLinkFields: [string, any[]][] = [];

    // Get form values from the raw item
    const formValues = rawItem.properties?.form_values || rawItem.form_values || {};

    // Look through all form values for record link fields
    Object.entries(formValues).forEach(([fieldId, value]) => {
      if (Array.isArray(value)) {
        // Check if this array contains record links
        const hasRecordLinks = value.some((item: any) => item && typeof item === "object" && item.record_id);

        if (hasRecordLinks) {
          console.log(`[FulcrumAdapter] Found record link field ${fieldId} with ${value.length} links`);
          recordLinkFields.push([fieldId, value]);
        }
      }
    });

    return recordLinkFields;
  }

  private getDefaultFulcrumMapping(): FieldMapping {
    return {
      id: "id",
      name: "form_values",
      description: "form_values",
      thumbnail: "photos",
      created_at: "created_at",
      last_updated_at: "updated_at",
      url: undefined,
      media_type: undefined,
      geometry: "geometry",
      coordinates: "geometry.coordinates"
    };
  }

  private getSimpleFulcrumName(item: any, endpoint?: ApiEndpoint): string {
    // Simple fallback name extraction
    if (item.name && typeof item.name === "string") {
      return item.name;
    }

    // PRIORITY 0: Endpoint config fieldMapping (config.json) – e.g. name: ["trip_name"]
    if (endpoint?.dataSourceConfig?.fieldMapping?.name) {
      const source = { ...(item.properties || {}), ...(item.fulcrum_data?.form_values || {}), ...item };
      const fields = endpoint.dataSourceConfig.fieldMapping.name;
      const arr = Array.isArray(fields) ? fields : [fields];
      const value = arr.length ? getFirstMatchingValueCI(source, arr) : null;
      if (value != null) {
        const str = extractStringValue(value).trim();
        if (str) return str;
      }
    }

    // PRIORITY 1: Try processed form values (readable field names)
    const processedFormValues = item.fulcrum_data?.form_values || {};
    const nameFields = ["Site Name", "name", "title", "location_name", "site_name", "animal name", "species"];
    for (const field of nameFields) {
      if (processedFormValues[field] && typeof processedFormValues[field] === "string") {
        console.log(`[FulcrumAdapter] Found name in processed form values: ${field} = ${processedFormValues[field]}`);
        return processedFormValues[field];
      }
    }

    // PRIORITY 2: Try to extract from raw form_values or properties
    const rawFormValues = item.properties?.form_values || item.form_values || {};
    for (const field of nameFields) {
      if (rawFormValues[field] && typeof rawFormValues[field] === "string") {
        return rawFormValues[field];
      }
    }

    // PRIORITY 3: Try top-level spread fields (from ...formValues)
    for (const field of nameFields) {
      if (item[field] && typeof item[field] === "string") {
        console.log(`[FulcrumAdapter] Found name in top-level fields: ${field} = ${item[field]}`);
        return item[field];
      }
    }

    return `Fulcrum Record ${item.id || item.properties?.id || "Unknown"}`;
  }

  private getSimpleFulcrumDescription(item: any, endpoint?: ApiEndpoint): string {
    // Simple fallback description extraction
    if (item.description && typeof item.description === "string") {
      return item.description;
    }

    // PRIORITY 0: Endpoint config fieldMapping (config.json) – e.g. description: ["trip_summary"]
    if (endpoint?.dataSourceConfig?.fieldMapping?.description) {
      const source = { ...(item.properties || {}), ...(item.fulcrum_data?.form_values || {}), ...item };
      const fields = endpoint.dataSourceConfig.fieldMapping.description;
      const arr = Array.isArray(fields) ? fields : [fields];
      const value = arr.length ? getFirstMatchingValueCI(source, arr) : null;
      if (value != null) {
        const str = extractStringValue(value).trim();
        if (str) return str;
      }
    }

    // PRIORITY 1: Try processed form values (readable field names)
    const processedFormValues = item.fulcrum_data?.form_values || {};
    const descFields = ["description", "notes", "comments", "details", "observations"];
    for (const field of descFields) {
      if (processedFormValues[field] && typeof processedFormValues[field] === "string") {
        console.log(`[FulcrumAdapter] Found description in processed form values: ${field} = ${processedFormValues[field]}`);
        return processedFormValues[field];
      }
    }

    // PRIORITY 2: Try raw form values
    const rawFormValues = item.properties?.form_values || item.form_values || {};
    for (const field of descFields) {
      if (rawFormValues[field] && typeof rawFormValues[field] === "string") {
        return rawFormValues[field];
      }
    }

    // PRIORITY 3: Try top-level spread fields (from ...formValues)
    for (const field of descFields) {
      if (item[field] && typeof item[field] === "string") {
        console.log(`[FulcrumAdapter] Found description in top-level fields: ${field} = ${item[field]}`);
        return item[field];
      }
    }

    return "";
  }

  private extractFulcrumPhotos(item: any): any[] {
    // Extract photos from various possible locations in Fulcrum data
    let photos: any[] = [];

    // Check direct photos array
    if (item.photos && Array.isArray(item.photos)) {
      photos = photos.concat(item.photos);
    }

    // Check properties.photos
    if (item.properties?.photos && Array.isArray(item.properties.photos)) {
      photos = photos.concat(item.properties.photos);
    }

    // Check fulcrum_data.photos
    if (item.fulcrum_data?.photos && Array.isArray(item.fulcrum_data.photos)) {
      photos = photos.concat(item.fulcrum_data.photos);
    }

    // Scan all property keys for Fulcrum photo arrays (e.g. form field IDs like 'b143')
    // Map-click / GeoJSON features often have photos under field IDs, not under "photos"
    if (item.properties && typeof item.properties === "object") {
      for (const value of Object.values(item.properties)) {
        if (Array.isArray(value) && value.length > 0 && value.some((p: any) => p && typeof p === "object" && (p.photo_id || p.id))) {
          photos = photos.concat(value);
          break; // Use first matching field only to avoid duplicates
        }
      }
    }

    // Normalize so each photo has .id (raw Fulcrum uses .photo_id) and accept either
    const normalized = photos
      .filter((photo) => photo && typeof photo === "object" && (photo.id || photo.photo_id))
      .map((photo) => ({ ...photo, id: photo.id || photo.photo_id }));
    return normalized;
  }

  private getFormId(item: any): string | undefined {
    return item.form_id || item.properties?.form_id || item.fulcrum_data?.form_id;
  }

  private constructFulcrumPhotoUrl(photoId: string, formId?: string, thumbnail: boolean = true): string {
    // Use the URL construction logic from SharedAdapterUtils
    return constructFulcrumPhotoUrl(photoId, formId, thumbnail);
  }

  private isFulcrumPhotoUrl(url: string): boolean {
    // Use the imported URL detection logic
    return isFulcrumPhotoUrl(url);
  }
}
